import React, { useEffect, useRef } from 'react';

import { Link } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useQueryClient } from '@tanstack/react-query';

import useResizeObserver from '../../../hooks/useResizeObserver';
import { useUiStore } from '../../../store';
import useUserData from '../../../hooks/useUserData';
import logo from '../../../assets/images/logo.png';

export default function Header() {
  const { logout } = useAuth0();

  const headerRef = useRef();

  const queryClient = useQueryClient();

  const { user } = useUserData();

  const { height } = useResizeObserver(headerRef);
  const setHeaderHeight = useUiStore((state) => state.setHeaderHeight);

  useEffect(() => {
    setHeaderHeight(height + 20);
  }, [height, setHeaderHeight]);

  return (
    <nav
      ref={headerRef}
      className="bg-white border-b border-gray-200 px-4 py-2.5 dark:bg-gray-800 dark:border-gray-700 fixed left-0 right-0 top-0 z-50 box-border"
    >
      <div className="flex flex-wrap justify-between items-center">
        <div className="flex justify-start items-center">
          <button
            data-drawer-target="sidebar-double"
            data-drawer-toggle="sidebar-double"
            aria-controls="sidebar-double"
            type="button"
            className="inline-flex items-center p-2 mr-3 text-sm text-gray-500 rounded-lg lg:hidden hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:text-gray-400 dark:hover:bg-gray-700 dark:focus:ring-gray-600"
          >
            <span className="sr-only">Open sidebar</span>
            <svg
              className="w-6 h-6"
              aria-hidden="true"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                clipRule="evenodd"
                fillRule="evenodd"
                d="M2 4.75A.75.75 0 012.75 4h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 4.75zm0 10.5a.75.75 0 01.75-.75h7.5a.75.75 0 010 1.5h-7.5a.75.75 0 01-.75-.75zM2 10a.75.75 0 01.75-.75h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 10z"
              ></path>
            </svg>
          </button>
          <Link to="/" className="flex mr-4">
            <img src={logo} className="mr-3 h-8" alt="Pixel Mortgage Logo" />
            <span className="self-center text-2xl font-semibold whitespace-nowrap dark:text-white">
              Pixel Mortgage
            </span>
          </Link>
        </div>
        <div className="flex items-center lg:order-2">
          <button
            type="button"
            className="flex mx-3 text-sm bg-gray-800 rounded-full md:mr-0 focus:ring-4 focus:ring-gray-300 dark:focus:ring-gray-600"
            id="user-menu-button"
            aria-expanded="false"
            data-dropdown-toggle="dropdown"
          >
            <span className="sr-only">Open user menu</span>
            <img
              className="w-8 h-8 rounded-full"
              src={user?.image}
              alt="user photo"
            />
          </button>

          <div
            className="hidden z-50 my-4 w-56 text-base list-none bg-white divide-y divide-gray-100 shadow dark:bg-gray-700 dark:divide-gray-600 rounded-xl"
            id="dropdown"
          >
            <div className="py-3 px-4">
              <span className="block text-sm font-semibold text-gray-900 dark:text-white">
                {user?.name}
              </span>
              <span className="block text-sm text-gray-900 truncate dark:text-white">
                {user?.email}
              </span>
            </div>
            {user?.withPassword && (
              <ul
                className="py-1 text-gray-700 dark:text-gray-300"
                aria-labelledby="dropdown"
              >
                <li>
                  <Link
                    to="/account"
                    className="block py-2 px-4 text-sm hover:bg-gray-100 dark:hover:bg-gray-600 dark:text-gray-400 dark:hover:text-white"
                  >
                    Account settings
                  </Link>
                </li>
              </ul>
            )}
            <ul
              className="py-1 text-gray-700 dark:text-gray-300"
              aria-labelledby="dropdown"
            >
              <li>
                <a
                  onClick={() => {
                    queryClient.clear();
                    localStorage.clear();
                    logout({
                      logoutParams: {
                        returnTo: window.location.origin,
                      },
                    });
                  }}
                  className="block py-2 px-4 text-sm hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white cursor-pointer"
                >
                  Sign out
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </nav>
  );
}
